<template>
  <main class="gx-bm-import">
    <header class="gx-bm-import__head">
      <button @click="$emit('back')">‹ 返回</button>
      <span>云库工作台 › 批量修改 › {{ title }}</span>
    </header>
    <section class="gx-bm-import__panel">
      <div class="gx-bm-import__icon">📥</div>
      <h1>{{ title }}</h1>
      <p>下载模板填写后上传，系统将异步校验并执行修改</p>
      <div class="gx-bm-import__actions">
        <button class="plain" :disabled="uploading" @click="downloadTemplate">下载模板</button>
        <button class="primary" :disabled="uploading" @click="$refs.file.click()">
          {{ uploading ? '上传中…' : '上传模板' }}
        </button>
        <input ref="file" type="file" accept=".csv,text/csv" hidden @change="onFileChange" />
      </div>
      <small>仅支持 CSV，最多 20,000 行，文件不超过 10MB</small>
      <div v-if="message" class="gx-bm-import__message" :class="messageType">
        {{ message }}
      </div>
    </section>
  </main>
</template>

<script>
import { uploadDirectOss } from '@/assets/js/utils/ossDirectUpload'
import { createCloudImportEditBatch } from '@/api/main/cloudBatchEdit'
import config from '@/assets/js/conf/config'

const MAX_SIZE = 10 * 1024 * 1024
const MAX_ROWS = 20000
const HEADERS = {
  spu: [
    '来源链接',
    '商品标题',
    '货号',
    '类目',
    '长度（单位：mm）',
    '宽度（单位：mm）',
    '高度（单位：mm）',
    '重量（单位：KG）',
    '备注'
  ],
  sku: ['来源链接', '颜色', '尺码', 'SKU价格', '库存', 'SKU编码']
}
const FIELD_LIMITS = {
  来源链接: 255,
  颜色: 255,
  尺码: 255,
  SKU编码: 255,
  备注: 255,
  商品标题: 500,
  货号: 500
}
// 当前 OSS 提供的两个模板地址相同；SPU 地址后续更新时只需替换对应常量。
const TEMPLATE_URLS = {
  spu: 'https://xinghaohuo.oss-cn-beijing.aliyuncs.com/importupdate/%E4%BF%AE%E6%94%B9%E4%BA%91%E5%95%86%E5%93%81%E5%BA%93%E5%95%86%E5%93%81%E5%AF%BC%E5%85%A5%E6%A8%A1%E6%9D%BF.csv',
  sku: 'https://xinghaohuo.oss-cn-beijing.aliyuncs.com/importupdate/%E4%BF%AE%E6%94%B9%E4%BA%91%E5%95%86%E5%93%81%E5%BA%93%E5%95%86%E5%93%81SKU%E5%AF%BC%E5%85%A5%E6%A8%A1%E6%9D%BF.csv'
}

export default {
  name: 'BatchModifyImport',
  props: { type: { type: String, required: true } },
  data: () => ({ uploading: false, message: '', messageType: '' }),
  computed: {
    title() {
      return this.type === 'sku' ? '导入修改商品信息 SKU' : '导入修改商品信息'
    }
  },
  methods: {
    downloadTemplate() {
      const url = TEMPLATE_URLS[this.type]
      if (!url) {
        this.message = '模板地址未配置'
        this.messageType = 'error'
        return
      }
      // 直接访问 OSS，避免经由后端读取和 Base64 转换模板。
      window.open(url, '_blank', 'noopener')
    },
    validateFile(file) {
      if (!file) return '请选择 CSV 文件'
      if (
        !/\.csv$/i.test(file.name) ||
        (file.type && file.type !== 'text/csv' && file.type !== 'application/vnd.ms-excel')
      )
        return '仅支持 CSV 文件'
      if (file.size > MAX_SIZE) return '文件大小不能超过 10MB'
      return ''
    },
    readCsvText(file, type) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          const buffer = reader.result
          const expected = HEADERS[type] || HEADERS.spu
          const decode = (encoding) => {
            try {
              return new TextDecoder(encoding).decode(buffer).replace(/^\uFEFF/, '')
            } catch (e) {
              return ''
            }
          }
          const candidates = ['utf-8', 'gb18030', 'gbk'].map(decode).filter(Boolean)
          const text =
            candidates.find((item) => item.indexOf(expected[0]) >= 0 && item.indexOf(expected[1]) >= 0) ||
            candidates[0] ||
            ''
          resolve(text)
        }
        reader.onerror = () => reject(new Error('读取文件失败'))
        reader.readAsArrayBuffer(file)
      })
    },
    parseCsv(text) {
      const records = []
      let row = [],
        value = '',
        quoted = false,
        afterQuote = false,
        rowStart = 1
      const push = () => {
        row.push(value)
        value = ''
      }
      const finish = () => {
        push()
        records.push({ row, rowStart })
        row = []
        rowStart += 1
      }
      for (let i = 0; i < text.length; i += 1) {
        const char = text[i]
        if (quoted) {
          if (char === '"' && text[i + 1] === '"') {
            value += '"'
            i += 1
          } else if (char === '"') {
            quoted = false
            afterQuote = true
          } else value += char
          continue
        }
        if (afterQuote) {
          if (char === ',') {
            push()
            afterQuote = false
            continue
          }
          if (char === '\n' || char === '\r') {
            if (char === '\r' && text[i + 1] === '\n') i += 1
            finish()
            afterQuote = false
            continue
          }
          if (/\s/.test(char)) continue
          throw new Error('CSV 存在非法引号或列结构')
        }
        if (char === '"' && value === '') {
          quoted = true
          continue
        }
        if (char === '"') throw new Error('CSV 存在非法引号或列结构')
        if (char === ',') {
          push()
          continue
        }
        if (char === '\n' || char === '\r') {
          if (char === '\r' && text[i + 1] === '\n') i += 1
          finish()
          rowStart += 1
          continue
        }
        value += char
      }
      if (quoted) throw new Error('CSV 存在未闭合引号')
      if (row.length || value !== '') finish()
      // 允许文件最后一条记录以换行结束，但不允许额外空数据行。
      return records
    },
    charLength(value) {
      return Array.from(String(value || '')).length
    },
    preflightCsv(file, type) {
      return this.readCsvText(file, type).then((text) => {
        if (!text.trim()) throw new Error('CSV 文件不能为空')
        const records = this.parseCsv(text)
        if (!records.length) throw new Error('CSV 文件不能为空')
        const expected = HEADERS[type] || HEADERS.spu
        const headers = records.shift().row.map((item) => String(item).trim())
        if (headers.length !== expected.length || headers.some((item, index) => item !== expected[index])) {
          throw new Error(`CSV 表头与模板不一致。实际表头：${headers.join(' | ')}；期望表头：${expected.join(' | ')}`)
        }
        if (!records.length) throw new Error('CSV 文件不能只有表头')
        if (records.length > MAX_ROWS) throw new Error('数据行不能超过 20,000 行')
        records.forEach((record, index) => {
          const rowNo = index + 2
          const values = record.row.map((item) => String(item).trim())
          if (values.length === 1 && values[0] === '') throw new Error(`第 ${rowNo} 行：数据行不能为空`)
          if (values.length !== expected.length) throw new Error(`第 ${rowNo} 行：数据列数不正确`)
          if (values.every((item) => item === '')) throw new Error(`第 ${rowNo} 行：数据行不能为空`)
          values.forEach((item, column) => {
            const header = expected[column]
            if (FIELD_LIMITS[header] && item !== '清空' && this.charLength(item) > FIELD_LIMITS[header])
              throw new Error(`第 ${rowNo} 行【${header}】：内容不能超过 ${FIELD_LIMITS[header]} 个字符`)
          })
          const data = {}
          expected.forEach((header, column) => {
            data[header] = values[column]
          })
          if (!data['来源链接']) throw new Error(`第 ${rowNo} 行【来源链接】：不能为空`)
          if (type === 'sku' && (!data['颜色'] || !data['尺码'])) throw new Error(`第 ${rowNo} 行：颜色和尺码不能为空`)
          const editable = expected.slice(type === 'sku' ? 3 : 1).some((header) => data[header] !== '')
          if (!editable) throw new Error(`第 ${rowNo} 行：至少填写一个修改字段`)
          const numeric =
            type === 'sku'
              ? ['SKU价格', '库存']
              : ['长度（单位：mm）', '宽度（单位：mm）', '高度（单位：mm）', '重量（单位：KG）']
          numeric.forEach((header) => {
            const item = data[header]
            if (!item || item === '清空') return
            if (!/^\d+(\.\d+)?$/.test(item) || Number(item) < 0 || (header === '库存' && !/^\d+$/.test(item)))
              throw new Error(`第 ${rowNo} 行【${header}】：格式必须为非负数字`)
          })
          if (type === 'spu' && data['类目'] && data['类目'] !== '清空' && !/^\d+$/.test(data['类目']))
            throw new Error(`第 ${rowNo} 行【类目】：必须为整数或“清空”`)
        })
        return records.length
      })
    },
    async onFileChange(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      this.message = ''
      const error = this.validateFile(file)
      if (error) {
        this.message = error
        this.messageType = 'error'
        return
      }
      try {
        await this.preflightCsv(file, this.type)
        this.uploading = true
        const result = await uploadDirectOss(file, {
          purpose: 'cloud-import-edit-' + this.type,
          policyUrl: `${config.baseApi}/merchant/cloud/import-edit/oss-upload-token`,
          policyParams: { type: this.type }
        })
        const response = await createCloudImportEditBatch({
          type: this.type,
          oss_url: result.url,
          file_name: file.name
        })
        if (!response || Number(response.code) !== 200)
          throw new Error((response && (response.message || response.msg)) || '导入任务创建失败')
        const batch = response.data || {}
        if (!batch.batch_id) throw new Error('导入任务创建接口未返回批次ID')
        this.message = `上传成功，批次 ${batch.batch_no || batch.batch_id} 已创建，后台将异步处理`
        this.messageType = 'success'
      } catch (e) {
        const payload = e && e.response && e.response.data
        const serverMessage = payload && (payload.message || payload.msg)
        this.message = serverMessage || e.message || '上传失败，请稍后重试'
        this.messageType = 'error'
      } finally {
        this.uploading = false
      }
    }
  }
}
</script>

<style scoped>
.gx-bm-import {
  min-height: calc(100vh - 56px);
  padding: 18px 22px;
  background: #fafafa;
}
.gx-bm-import__head {
  display: flex;
  gap: 10px;
  align-items: center;
  color: #999;
  font-size: 12px;
}
.gx-bm-import__head button {
  height: 28px;
  padding: 0 12px;
  border: 1px solid #e0e0ec;
  border-radius: 4px;
  background: #fff;
  color: #666;
  cursor: pointer;
}
.gx-bm-import__panel {
  display: flex;
  min-height: 390px;
  margin: 28px auto;
  padding: 30px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px solid #e0e0ec;
  border-radius: 8px;
  background: #fff;
  text-align: center;
}
.gx-bm-import__icon {
  font-size: 44px;
}
.gx-bm-import h1 {
  margin: 10px 0 6px;
  color: #1a1a2e;
  font-size: 20px;
}
.gx-bm-import p {
  margin: 0 0 24px;
  color: #999;
  font-size: 12px;
}
.gx-bm-import__actions {
  display: flex;
  gap: 12px;
}
.gx-bm-import__actions button {
  min-width: 120px;
  height: 34px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
}
.gx-bm-import__actions .plain {
  border: 1px solid #1677ff;
  background: #fff;
  color: #1677ff;
}
.gx-bm-import__actions .primary {
  border: 0;
  background: #1677ff;
  color: #fff;
}
.gx-bm-import__actions button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.gx-bm-import small {
  margin-top: 14px;
  color: #aaa;
  font-size: 11px;
}
.gx-bm-import__message {
  margin-top: 18px;
  font-size: 12px;
}
.gx-bm-import__message.success {
  color: #52c41a;
}
.gx-bm-import__message.error {
  color: #c8443c;
}
</style>
